import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from './vital-codes';
import { computeBmi as bmiFromTyped } from '@/encounter/lib/to-vitals-request';
import { waistToHipFrom } from '@/encounter/lib/to-vitals-row';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/**
 * Client-side derived-vital computation. Mirrors Flutter
 * `vital_data.dart:bmi(list)` + `waistToHipRatio(list)` (lines
 * 130-151) and the underlying `calculateBmi` / `calculateWaistToHipRatio`
 * in `scrips_core/lib/utils/utils.dart:554-575`.
 *
 * Used by the vitals history drawer (PROD-795 PR 4) to mirror the
 * Flutter Historical matrix view, which displays a BMI column
 * computed client-side from each encounter's height + weight readings
 * — the server doesn't persist BMI as a separate row.
 *
 * Formulas (verbatim from Flutter):
 *   BMI   = round((weight * 10000) / (height * height))
 *           where weight in kg, height in cm
 *   W/H   = waist / hip   (full decimal, not rounded)
 *
 * Returns null when the required inputs are missing or zero.
 *
 * INTENTIONAL Flutter divergence at the zero-value edge (Andrew
 * confirmed 2026-05-15): Flutter's `calculateBmi` returns `0` when
 * either input is 0, causing the cell to display "BMI = 0" — which
 * is clinically meaningless (no patient has height 0). React returns
 * null in that case so the cell renders "—", which is cleaner UX.
 * The MISSING-input path matches Flutter exactly (both → null →
 * no row). Only the zero-input edge differs.
 */

function parseNumeric(value: string | null | undefined): number | null {
  if (value == null || value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function findReadingValue(
  readings: PatientVital[],
  code: string,
): number | null {
  const reading = readings.find((r) => r.code?.code === code);
  return parseNumeric(reading?.quantity?.value);
}

/**
 * Compute BMI from height + weight readings. Returns null if either
 * input is missing or non-positive (matches Flutter `calculateBmi`
 * guard, but returns null instead of 0 so the UI can render "—").
 *
 * Result is a whole number (Flutter `.roundToDouble()`).
 */
export function computeBmi(readings: PatientVital[]): number | null {
  const weight = findReadingValue(readings, VITAL_CODES.WEIGHT);
  const height = findReadingValue(readings, VITAL_CODES.HEIGHT);
  if (!weight || !height) return null;
  return Math.round((weight * 10000) / (height * height));
}

/**
 * Compute waist-to-hip ratio. Server typically returns this already
 * (the curl test data shows `quantityValue: "1"` for code 56115-9),
 * but this helper is the fallback when it's missing — matches Flutter
 * parity for the History view.
 */
export function computeWaistToHipRatio(readings: PatientVital[]): number | null {
  const waist = findReadingValue(readings, VITAL_CODES.WAIST);
  const hip = findReadingValue(readings, VITAL_CODES.HIP);
  if (!waist || !hip) return null;
  return waist / hip;
}

/**
 * Returns a new PatientVital array with synthetic BMI and Waist-to-Hip
 * Ratio rows injected from client-side computation. Existing rows are
 * left untouched; we only synthesize when the server didn't provide a
 * value.
 *
 * History-view-specific (matches Flutter `vitals_history_widget.dart`).
 * The in-tab grid does NOT inject these — Flutter's in-tab cell also
 * shows empty BMI, so React mirrors that for parity.
 */
export function injectDerivedReadings(
  readings: PatientVital[],
): PatientVital[] {
  const result = [...readings];

  const existingBmi = result.find((r) => r.code?.code === VITAL_CODES.BMI);
  const hasBmiValue =
    !!existingBmi?.quantity?.value &&
    existingBmi.quantity.value.trim() !== '';
  if (!hasBmiValue) {
    const bmi = computeBmi(readings);
    if (bmi != null) {
      result.push(makeSynthetic(VITAL_CODES.BMI, String(bmi)));
    }
  }

  const existingWhr = result.find(
    (r) => r.code?.code === VITAL_CODES.WAIST_TO_HIP_RATIO,
  );
  const hasWhrValue =
    !!existingWhr?.quantity?.value &&
    existingWhr.quantity.value.trim() !== '';
  if (!hasWhrValue) {
    const whr = computeWaistToHipRatio(readings);
    if (whr != null) {
      // Format to 2 decimals to keep the display tidy (Flutter renders
      // the raw double, often as `0.85`).
      result.push(makeSynthetic(VITAL_CODES.WAIST_TO_HIP_RATIO, whr.toFixed(2)));
    }
  }

  return result;
}

function makeSynthetic(code: string, value: string): PatientVital {
  // Mark as `derived-<code>` identifire so React keys + dev inspection
  // make it obvious this row was computed client-side rather than
  // returned by the server.
  return {
    identifire: `derived-${code}`,
    status: null,
    code: {
      code,
      Definition: null,
      display: null,
      system: null,
      description: null,
      shortName: null,
    },
    quantity: { value, unit: null, system: null, code },
    subject: null,
    encounter: null,
    category: null,
    dataAbsentReason: null,
    deleteProvider: null,
    effectiveDateTime: null,
    vitalNormalStatus: null,
    vitalAbnormalPercentage: null,
    lmp: null,
    hcm: null,
    isDeleted: false,
    createdDate: null,
    isMenstrual: null,
    isBreastFeeding: null,
    extraData: null,
  };
}

/**
 * `vitalsComputedLive` (ON on dev; Samer, 3 Nov 20:44: "waist to hip computation is not working"): the reading a COMPUTED vital
 * cell (BMI, waist-to-hip ratio) shows, with the Edit vitals dialog's own rules in BOTH views: BMI `computeBmi` (one decimal), the
 * ratio `waistToHipFrom` (two decimals, 40 hip / 30 waist -> 0.75).
 *   - Editing: from what is typed, each input falling back to its stored reading when its field is empty, so retyping one value
 *     never blanks a ratio the stored ones still give.
 *   - At rest: a stored reading wins; with none stored, the ratio is derived from the stored hip and waist (BMI stays as stored).
 * Display only: the result is never put in the form or the save. OFF (`?x=-vitals-computed-live`): the stored reading, as before.
 */
export function computedVitalReading(
  code: string,
  reading: PatientVital | null,
  allReadings: PatientVital[],
  typed?: Record<string, string>,
): PatientVital | null {
  if (code !== VITAL_CODES.BMI && code !== VITAL_CODES.WAIST_TO_HIP_RATIO) return reading;
  if (!isEncounterFlagOn('vitalsComputedLive')) return reading;
  const stored = (c: string) => storedValue(allReadings, c);
  if (typed) {
    const input = (c: string) => (typed[c]?.trim() ? typed[c] : stored(c));
    const value =
      code === VITAL_CODES.BMI
        ? bmiFromTyped(input(VITAL_CODES.HEIGHT), input(VITAL_CODES.WEIGHT))
        : ratioFrom(input(VITAL_CODES.WAIST), input(VITAL_CODES.HIP));
    return value ? makeSynthetic(code, value) : null;
  }
  if (reading?.quantity?.value?.trim()) return reading;
  if (code !== VITAL_CODES.WAIST_TO_HIP_RATIO) return reading;
  const ratio = ratioFrom(stored(VITAL_CODES.WAIST), stored(VITAL_CODES.HIP));
  return ratio ? makeSynthetic(code, ratio) : reading;
}

/** The stored reading's raw value for a code ('' when none). */
function storedValue(all: PatientVital[], code: string): string {
  return all.find((r) => r.code?.code === code && !r.isDeleted)?.quantity?.value ?? '';
}

/** The one waist-to-hip rule both views use: the dialog's `waistToHipFrom` (two decimals). */
function ratioFrom(waist: string, hip: string): string {
  return waistToHipFrom(Number(waist) || undefined, Number(hip) || undefined) ?? '';
}
